import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import { regionDisplayName } from "@/lib/regionLabel";
import { regionKeys, regionService } from "@/services/regionService";

export type AdminRegionFilterValue = string | null;

interface AdminRegionFilterProps {
  value: AdminRegionFilterValue;
  onChange: (value: AdminRegionFilterValue) => void;
  /**
   * Count items per option key (must match `valueKey`: code or id).
   * `all` is the total for the count base.
   */
  counts?: Record<string, number>;
  /** Option value is region code (default) or id for server-side region_id filters. */
  valueKey?: "code" | "id";
}

export function AdminRegionFilter({
  value,
  onChange,
  counts,
  valueKey = "code",
}: AdminRegionFilterProps) {
  const { t } = useTranslation("common");
  const { data: regions = [] } = useQuery({
    queryKey: regionKeys.active(),
    queryFn: () => regionService.listActive(),
  });

  const options = useMemo(() => {
    const regionKey = (region: (typeof regions)[number]) =>
      valueKey === "id" ? region.id : region.code;
    const allCount =
      counts?.all ??
      regions.reduce(
        (sum, region) => sum + (counts?.[regionKey(region)] ?? 0),
        0,
      );
    return [
      {
        value: null as AdminRegionFilterValue,
        label: t("region.filterAll"),
        count: counts ? allCount : undefined,
      },
      ...regions.map((region) => ({
        value: regionKey(region) as AdminRegionFilterValue,
        label: regionDisplayName(region.code, t, region.name),
        count: counts?.[regionKey(region)],
      })),
    ];
  }, [counts, regions, t, valueKey]);

  if (regions.length === 0) {
    return null;
  }

  return (
    <AdminFilterMenu
      icon={MapPin}
      name={t("region.filterLabel")}
      value={value}
      defaultValue={null}
      options={options}
      onChange={onChange}
    />
  );
}
